/**
 * Copying text to the clipboard, in the three places this app runs.
 *
 * `navigator.clipboard.writeText` is the whole story on a modern browser over
 * HTTPS or localhost — and is exactly the thing that is missing everywhere else
 * Kobo runs. The Clipboard API is gated on a *secure context*, so a PWA opened
 * over plain HTTP on the LAN has no `navigator.clipboard` at all, and older
 * WKWebView builds (the Tauri shell on an older macOS) expose it but reject the
 * write when the call is not inside a user gesture the engine recognises.
 *
 * The fallback is the pre-Clipboard-API technique: a hidden textarea, a
 * selection, and `document.execCommand("copy")`. It is deprecated and it still
 * works in every engine here, which is the only property that matters for a
 * "copy this token and send it to a colleague" button.
 *
 * Returns whether the text is on the clipboard, and never throws: a caller
 * showing a toast needs to know which one to show, not to guard a try/catch.
 */
export async function copyText(value: string): Promise<boolean> {
  if (value === "") {
    return false;
  }

  if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(value);
      return true;
    } catch {
      // Fall through: a rejection here is a permission or secure-context
      // problem, and the legacy path is not subject to either.
    }
  }

  return copyWithExecCommand(value);
}

function copyWithExecCommand(value: string): boolean {
  if (typeof document === "undefined" || !document.body) {
    return false;
  }

  const area = document.createElement("textarea");
  area.value = value;
  // Off-screen rather than `display: none`: a hidden element cannot hold a
  // selection, and the copy would silently succeed having copied nothing.
  area.setAttribute("readonly", "");
  area.style.position = "fixed";
  area.style.top = "-1000px";
  area.style.opacity = "0";
  document.body.appendChild(area);

  const previous = document.activeElement as HTMLElement | null;
  try {
    area.select();
    area.setSelectionRange(0, value.length);
    return document.execCommand("copy");
  } catch {
    return false;
  } finally {
    document.body.removeChild(area);
    // Put the caret back where the user left it — usually the button they just
    // pressed, which keeps the focus ring where they are looking.
    previous?.focus?.({ preventScroll: true });
  }
}
